import React, { startTransition } from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import type { PhoneNumberWithCountryCode } from '@gov-design-system-ce/react-form';
import type { PersonCommunicationData } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { FormCheckboxProps, FormPhoneNumberProps } from '@gov-nx/ui/types';
import {
	Button,
	FormCheckbox,
	FormWidthElementLayout,
	PhoneNumberInput,
} from '@gov-nx/ui/web';
import { phoneShapeReadable } from '@gov-nx/utils/common';
import { CommunicationDataFieldLayout } from './CommunicationDataFieldLayout';

interface UseCommunicationDataPhoneProps {
	personCommunicationData: PersonCommunicationData;
	fields: {
		_usePhone: FormCheckboxProps;
		phone: FormPhoneNumberProps;
	};
	wcagLabels: {
		edit: string;
		cancel: string;
	};
	phone?: PhoneNumberWithCountryCode;
	onReset: () => void;
	isRequired?: boolean;
}

export const UseCommunicationDataPhone = ({
	personCommunicationData,
	fields,
	onReset,
	phone,
	wcagLabels,
	isRequired,
}: UseCommunicationDataPhoneProps) => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const isEditingPhone = personCommunicationData.control.isEditingPhone.value;
	const checkboxContent = (
		<>
			<FormCheckbox {...fields._usePhone} />
			{isRequired && <div className="gov-color--text-status-error">*</div>}
		</>
	);

	const editPhoneButton = (
		<Button
			onClick={personCommunicationData.control.isEditingPhone.setTrue}
			isDisabled={fields.phone.field.isDisabled}
			aria-label={wcagLabels.edit}
			color="primary"
			size="s"
			type="base"
			iconEnd={
				<GovIcon
					name="pencil-square"
					type="templates"
				/>
			}
		/>
	);

	return (
		<CommunicationDataFieldLayout
			isEditing={isEditingPhone}
			checkboxContent={checkboxContent}
			editButton={editPhoneButton}
			valueContent={
				<strong>
					{phone && phone.number ? (
						phoneShapeReadable(phone)
					) : (
						<i>{t('empty')}</i>
					)}
				</strong>
			}
			editContent={
				<div className="flex flex-row gap-2 [ md:ml-10 ]">
					<FormWidthElementLayout type="xl">
						<PhoneNumberInput
							{...fields.phone}
							label={{ className: 'hidden!' }}
						/>
					</FormWidthElementLayout>

					<Button
						color="primary"
						type="base"
						size="m"
						onClick={() => {
							personCommunicationData.control.isEditingPhone.setFalse();
							startTransition(() => {
								onReset();
							});
						}}
						isDisabled={fields.phone.field.isDisabled}
						aria-label={wcagLabels.cancel}
						iconEnd={
							<GovIcon
								name="x-lg"
								type="components"
							/>
						}
					/>
				</div>
			}
		/>
	);
};
